import { useEffect, useState } from "react";
import { X } from "lucide-react";
import mascotAsset from "@/assets/mascot-julian.png.asset.json";
import { cn } from "@/lib/utils";
import { tap } from "@/lib/haptics";

/**
 * Floating mascot pop-up. Appears bottom-right after a short delay on every
 * page load, waves with a subtle bobble animation, and can be dismissed
 * (the dismissal persists for the session so it doesn't get annoying).
 *
 * Pure CSS animation — no framer-motion dependency.
 */
export function Mascot({
  message = "¡Vamos Argentina!",
  cta,
  delayMs = 10000,
  storageKey = "mascot:dismissed:v2",
}: {
  message?: string;
  cta?: { label: string; onClick: () => void };
  delayMs?: number;
  storageKey?: string;
}) {
  const [phase, setPhase] = useState<"hidden" | "entering" | "open" | "leaving">("hidden");

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (sessionStorage.getItem(storageKey) === "1") return;
    } catch {
      /* private mode */
    }
    const t = window.setTimeout(() => setPhase("entering"), delayMs);
    return () => window.clearTimeout(t);
  }, [delayMs, storageKey]);

  useEffect(() => {
    if (phase !== "entering") return;
    const t = window.setTimeout(() => setPhase("open"), 50);
    return () => window.clearTimeout(t);
  }, [phase]);

  const dismiss = () => {
    tap(8);
    setPhase("leaving");
    try {
      sessionStorage.setItem(storageKey, "1");
    } catch {
      /* ignore */
    }
    window.setTimeout(() => setPhase("hidden"), 280);
  };

  if (phase === "hidden") return null;

  const visible = phase === "open";

  return (
    <div
      aria-live="polite"
      className={cn(
        // Sits above bottom nav (h ~64px + safe area). Right-anchored.
        "pointer-events-none fixed bottom-24 right-3 z-40 sm:right-5 sm:bottom-28",
      )}
    >
      <div
        className={cn(
          "pointer-events-auto relative flex flex-row-reverse items-end gap-2 transition-all duration-500 ease-[cubic-bezier(.2,.9,.25,1.2)]",
          visible
            ? "translate-x-0 translate-y-0 opacity-100"
            : "translate-x-4 translate-y-3 opacity-0",
        )}
      >
        {/* Mascot figure */}
        <button
          type="button"
          onClick={() => tap(8)}
          aria-label="Argentinian.Soccer mascot"
          className="relative shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-sky/70 rounded-full"
        >
          {/* Shadow plate */}
          <span
            aria-hidden
            className="absolute inset-x-2 -bottom-1 h-3 rounded-full bg-navy/35 blur-md"
          />
          <img
            src={mascotAsset.url}
            alt=""
            width={120}
            height={170}
            decoding="async"
            loading="lazy"
            className="relative h-[110px] w-auto drop-shadow-[0_18px_18px_rgba(10,26,61,0.35)] mascot-bobble select-none"
            draggable={false}
          />
        </button>

        {/* Speech bubble */}
        <div className="relative mb-2 max-w-[180px] rounded-2xl rounded-br-sm bg-card px-3 py-2 text-card-foreground shadow-[0_10px_30px_-12px_rgba(10,26,61,0.45)] ring-1 ring-border">
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss mascot"
            className="absolute -right-2 -top-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-navy text-primary-foreground shadow ring-1 ring-white/20 transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky/70"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <p className="font-display text-[0.95rem] leading-tight text-navy">{message}</p>
          {cta && (
            <button
              type="button"
              onClick={() => {
                tap(14);
                cta.onClick();
              }}
              className="mt-1.5 font-condensed text-[0.65rem] font-bold uppercase tracking-widest text-primary hover:text-sky-deep"
            >
              {cta.label} →
            </button>
          )}
          {/* Bubble tail */}
          <span
            aria-hidden
            className="absolute -right-1 bottom-2 h-3 w-3 rotate-45 bg-card ring-1 ring-border"
          />
        </div>
      </div>
    </div>
  );
}
